import {
  Box,
  Collapse,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography
} from '@mui/material';
import React from 'react';
import { MenuItem, PanelLeftMenuProps } from './PanelLeftMenuWrapper';
import LastPageOutlinedIcon from '@mui/icons-material/LastPageOutlined';
import FirstPageOutlinedIcon from '@mui/icons-material/FirstPageOutlined';
import UnfoldLessOutlinedIcon from '@mui/icons-material/UnfoldLessOutlined';
import UnfoldMoreOutlinedIcon from '@mui/icons-material/UnfoldMoreOutlined';
import PrimaryDivider from '../decoration/PrimaryDivider';

/**
 * Collapseable left menu navigation panel
 * @param {string} header Title of the left menu
 * @param {Array<MenuItem>} items Navigation items available to the user
 * @param {void} handleMenuSelect Send selected navigation item back to parent
 * @returns {JSX.Element}
 */
const PanelLeftMenu = ({ header, items, handleMenuSelect }: PanelLeftMenuProps) => {
  const [expanded, setExpanded] = React.useState<boolean>(true);
  const [expandedItem, setExpandedItem] = React.useState<MenuItem | null>(null);

  const handleExpandMenu = (expand: boolean): void => {
    setExpanded(expand);
  };

  const handleExpandItem = (item: MenuItem): void => {
    setExpandedItem(item.id === expandedItem?.id ? null : item);
  };

  return (
    <Box
      sx={{
        ...{ ml: -3, mt: -3, mb: -3, mr: 3 },
        minHeight: '100vh',
        backgroundColor: '#FAFAFA',
        border: '1px solid rgba(0, 0, 0, 0.25)',
        minWidth: '100px',
        maxWidth: '320px',
        width: expanded ? '30%' : '5%',
        transition: 'width 0.3s ease',
        overflow: 'hidden'
      }}
    >
      <Box
        sx={{
          mx: 4,
          my: 3,
          display: 'flex',
          alignItems: 'center',
          justifyContent: expanded ? 'start' : 'center'
        }}
      >
        {expanded && (
          <>
            <Typography sx={{ fontSize: '1.3rem', whiteSpace: 'nowrap' }}>{header}</Typography>
          </>
        )}
        <IconButton onClick={() => handleExpandMenu(!expanded)} sx={{ ml: 'auto' }}>
          {expanded ? <FirstPageOutlinedIcon /> : <LastPageOutlinedIcon />}
        </IconButton>
      </Box>
      <PrimaryDivider opacity={0.6} />
      <Box>
        <List>
          {items.map((item: MenuItem) => (
            <ListItem key={item.label} disablePadding sx={{ minHeight: '65px', display: 'block' }}>
              {item.child_items ? (
                <>
                  <ListItemButton
                    onClick={() => handleExpandItem(item)}
                    sx={{ justifyContent: 'center' }}
                  >
                    <ListItemIcon sx={{ justifyContent: 'center' }}>{item.icon}</ListItemIcon>
                    {expanded && (
                      <ListItemText sx={{ whiteSpace: 'nowrap' }} primary={item.label} />
                    )}
                    {expandedItem?.id === item.id ? (
                      <UnfoldLessOutlinedIcon />
                    ) : (
                      <UnfoldMoreOutlinedIcon />
                    )}
                  </ListItemButton>
                  <Collapse in={expandedItem?.id === item.id} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                      {item.child_items.map((item: MenuItem) => (
                        <ListItemButton
                          key={item.id}
                          sx={{
                            justifyContent: 'center',
                            minHeight: '65px',
                            ml: expanded ? 4 : 0
                          }}
                          onClick={() => handleMenuSelect(item)}
                        >
                          <ListItemIcon sx={{ justifyContent: 'center' }}>{item.icon}</ListItemIcon>
                          {expanded && (
                            <ListItemText sx={{ whiteSpace: 'nowrap' }} primary={item.label} />
                          )}
                        </ListItemButton>
                      ))}
                    </List>
                  </Collapse>
                </>
              ) : (
                <ListItemButton
                  sx={{ justifyContent: 'center' }}
                  onClick={() => handleMenuSelect(item)}
                >
                  <ListItemIcon sx={{ justifyContent: 'center' }}>{item.icon}</ListItemIcon>
                  {expanded && <ListItemText sx={{ whiteSpace: 'nowrap' }} primary={item.label} />}
                </ListItemButton>
              )}
            </ListItem>
          ))}
        </List>
      </Box>
    </Box>
  );
};

export default PanelLeftMenu;
